/**
 * The assets browser, shown in the drawer and in the docked Assets window: files and presets
 * are both assets. The tree on the left (files of the project and of this computer, then the
 * preset libraries), the folder's tiles on the right (`FileGallery` or `PresetGallery`), the
 * asset previewed over them, or the form while a preset is being made or edited.
 */
import { Show, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { createFolders } from "./folders";
import { FolderTree } from "./FolderTree";
import { PresetForm } from "./PresetForm";
import { PresetGallery } from "./PresetGallery";
import { FileGallery } from "./FileGallery";
import { AssetPreview } from "./AssetPreview";
import { createFileTools } from "./fileTools";

/** What the browser's host (drawer or window) adds. */
export interface PresetBrowserProps {
  /** Buttons at the end of the gallery's header (dock or undock). */
  tools?: JSX.Element;
  /** A preset was placed on the graph. */
  onPlaced?: () => void;
}

/** The presets browser. */
export function PresetBrowser(props: PresetBrowserProps): JSX.Element {
  const { presets, assets, nodeAdder } = useApp();
  const folders = createFolders(presets);
  const files = createFileTools({ assets, nodeAdder, placed: props.onPlaced });
  return (
    <div class="presets">
      <Show
        when={presets.draft()}
        keyed
        fallback={
          <div class="pbrowser">
            <FolderTree folders={folders} files={files} />
            <Show when={assets.place()} fallback={<PresetGallery folders={folders} tools={props.tools} onPlaced={props.onPlaced} />}>
              <FileGallery tools={files} extra={props.tools} />
            </Show>
            <AssetPreview />
          </div>
        }
      >
        {(draft) => <PresetForm draft={draft} />}
      </Show>
    </div>
  );
}
